import React, { useState, useEffect, useCallback } from "react";

type Difficulty = "easy" | "medium" | "hard" | "extreme";

type GameState = "setup" | "playing" | "won" | "lost";

interface DifficultyConfig {
  label: string;
  min: number;
  max: number;
  maxAttempts: number;
  color: string;
  timeLimit: number; // in seconds
}

const difficultyConfigs: Record<Difficulty, DifficultyConfig> = {
  easy: {
    label: "Easy",
    min: 1,
    max: 100,
    maxAttempts: 10,
    color: "#4ade80", // green
    timeLimit: 60,
  },
  medium: {
    label: "Medium",
    min: 1,
    max: 500,
    maxAttempts: 12,
    color: "#60a5fa", // blue
    timeLimit: 60,
  },
  hard: {
    label: "Hard",
    min: 1,
    max: 1000,
    maxAttempts: 15,
    color: "#f59e0b", // amber
    timeLimit: 90,
  },
  extreme: {
    label: "Extreme",
    min: 1,
    max: 10000,
    maxAttempts: 20,
    color: "#ef4444", // red
    timeLimit: 120,
  },
};

type Props = {};

export default function GuessNumberGamePage({}: Props) {
  const [difficulty, setDifficulty] = useState<Difficulty>("medium");
  const [targetNumber, setTargetNumber] = useState<number>(0);
  const [guess, setGuess] = useState<string>("");
  const [attempts, setAttempts] = useState<number>(0);
  const [feedback, setFeedback] = useState<string>("");
  const [gameState, setGameState] = useState<GameState>("setup");
  const [guessHistory, setGuessHistory] = useState<
    { value: number; result: string }[]
  >([]);
  const [score, setScore] = useState<number>(0);
  const [highScore, setHighScore] = useState<number>(0);
  const [consecutiveWins, setConsecutiveWins] = useState<number>(0);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [hintUsed, setHintUsed] = useState<boolean>(false);

  const config = difficultyConfigs[difficulty];

  // Generate a new random number
  const generateNumber = () => {
    return (
      Math.floor(Math.random() * (config.max - config.min + 1)) + config.min
    );
  };

  // Start a new game
  const startGame = () => {
    const newTarget = generateNumber();
    setTargetNumber(newTarget);
    setGuess("");
    setAttempts(0);
    setFeedback("");
    setGameState("playing");
    setGuessHistory([]);
    setHintUsed(false);
    setTimeLeft(config.timeLimit);
    console.log("New game started with target:", newTarget); // for debugging
  };

  // Handle guess submission
  const handleGuess = useCallback(() => {
    const numGuess = Number(guess);

    if (isNaN(numGuess) || numGuess < config.min || numGuess > config.max) {
      setFeedback(
        `Please enter a valid number between ${config.min} and ${config.max}`
      );
      return;
    }

    const newAttempts = attempts + 1;
    setAttempts(newAttempts);

    let result = "";
    if (numGuess === targetNumber) {
      // Calculate score based on difficulty, attempts, time left, and if hint was used
      const difficultyMultiplier = {
        easy: 1,
        medium: 2,
        hard: 5,
        extreme: 10,
      }[difficulty];

      const attemptsBonus = Math.max(0, config.maxAttempts - newAttempts) * 10;
      const timeBonus = Math.floor(timeLeft / 2);
      const hintPenalty = hintUsed ? 0.5 : 1;

      const newScore = Math.floor(
        (1000 * difficultyMultiplier + attemptsBonus + timeBonus) * hintPenalty
      );

      setScore(score + newScore);
      setHighScore(Math.max(highScore, score + newScore));
      setConsecutiveWins(consecutiveWins + 1);
      setGameState("won");
      setFeedback(
        `Congratulations! You guessed the number in ${newAttempts} attempts and earned ${newScore} points!`
      );
      result = "correct";
    } else {
      const hint = numGuess < targetNumber ? "too low" : "too high";
      result = hint;

      if (newAttempts >= config.maxAttempts) {
        setGameState("lost");
        setConsecutiveWins(0);
        setFeedback(
          `Game over! You've used all ${config.maxAttempts} attempts. The number was ${targetNumber}.`
        );
      } else {
        setFeedback(
          `Your guess is ${hint}. ${
            config.maxAttempts - newAttempts
          } attempts remaining.`
        );
      }
    }

    setGuessHistory([...guessHistory, { value: numGuess, result }]);
    setGuess("");
  }, [
    guess,
    config.min,
    config.max,
    attempts,
    targetNumber,
    difficulty,
    timeLeft,
    hintUsed,
    score,
    highScore,
    consecutiveWins,
    guessHistory,
    config.maxAttempts,
  ]);

  // Use a hint (narrows down the range)
  const useHint = () => {
    if (hintUsed) return;

    // Find a range that includes the target but is smaller than the full range
    const range = Math.floor((config.max - config.min) / 4);
    const lowerBound = Math.max(config.min, targetNumber - range);
    const upperBound = Math.min(config.max, targetNumber + range);

    setFeedback(`Hint: The number is between ${lowerBound} and ${upperBound}`);
    setHintUsed(true);
  };

  // Timer effect
  useEffect(() => {
    if (gameState !== "playing" || timeLeft <= 0) return;

    const timer = setTimeout(() => {
      if (timeLeft <= 0) {
        setGameState("lost");
        setConsecutiveWins(0);
        setFeedback(`Time's up! The number was ${targetNumber}.`);
      } else {
        setTimeLeft(timeLeft - 1);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft, gameState, targetNumber]);

  // Keyboard shortcut for Enter key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && gameState === "playing") {
        handleGuess();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gameState, guess, handleGuess]);

  // Format the time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  // Percentage of guessing range for visualization
  const getPercentInRange = (num: number) => {
    return ((num - config.min) / (config.max - config.min)) * 100;
  };

  // Styling constants
  const primaryColor = config.color;
  const backgroundColor = "#111827"; // dark gray
  const textColor = "#f3f4f6"; // light gray
  const secondaryColor = "#6b7280"; // medium gray

  return (
    <div
      style={{
        overflowX: "hidden",
        color: textColor,
        backgroundColor: backgroundColor,
      }}
    >
      <h1
        style={{
          fontSize: "2.5rem",
          fontWeight: "bold",
          marginBottom: "5px",
          background: `linear-gradient(90deg, ${primaryColor}, #8b5cf6)`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          textAlign: "center",
        }}
      >
        Guess The Number
      </h1>

      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          margin: "10px 0 20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#1f2937",
            padding: "8px 12px",
            borderRadius: "16px",
            fontSize: "0.9rem",
          }}
        >
          <span style={{ fontWeight: "bold", marginRight: "5px" }}>Score:</span>
          <span>{score}</span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#1f2937",
            padding: "8px 12px",
            borderRadius: "16px",
            fontSize: "0.9rem",
          }}
        >
          <span style={{ fontWeight: "bold", marginRight: "5px" }}>
            High Score:
          </span>
          <span>{highScore}</span>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#1f2937",
            padding: "8px 12px",
            borderRadius: "16px",
            fontSize: "0.9rem",
          }}
        >
          <span style={{ fontWeight: "bold", marginRight: "5px" }}>Wins:</span>
          <span>{consecutiveWins}</span>
        </div>
      </div>
      <div style={{ padding: "16px", width: "100%" }}>
        <div
          style={{
            width: "100%",
            maxWidth: "600px",
            backgroundColor: "#1f2937",
            borderRadius: "20px",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.3)",
            padding: "10px",
            marginBottom: "20px",
          }}
        >
          {gameState === "setup" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <h2
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  textAlign: "center",
                  marginBottom: "10px",
                }}
              >
                Select Difficulty
              </h2>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                  gap: "10px",
                  marginBottom: "20px",
                }}
              >
                {(Object.keys(difficultyConfigs) as Difficulty[]).map(
                  (diff) => (
                    <button
                      key={diff}
                      onClick={() => setDifficulty(diff)}
                      style={{
                        backgroundColor:
                          difficulty === diff
                            ? difficultyConfigs[diff].color
                            : "rgba(255, 255, 255, 0.1)",
                        color: difficulty === diff ? "#000" : textColor,
                        border: "none",
                        padding: "12px 15px",
                        borderRadius: "12px",
                        fontSize: "0.9rem",
                        fontWeight: "bold",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                      }}
                    >
                      {difficultyConfigs[diff].label}
                    </button>
                  )
                )}
              </div>

              <div
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  padding: "15px",
                  borderRadius: "15px",
                  marginBottom: "20px",
                }}
              >
                <h3
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    marginBottom: "10px",
                  }}
                >
                  {difficultyConfigs[difficulty].label} Mode
                </h3>
                <ul
                  style={{
                    paddingLeft: "20px",
                    fontSize: "0.9rem",
                    color: "rgba(255, 255, 255, 0.8)",
                  }}
                >
                  <li>
                    Range: {config.min} to {config.max}
                  </li>
                  <li>Attempts: {config.maxAttempts}</li>
                  <li>Time Limit: {formatTime(config.timeLimit)}</li>
                </ul>
              </div>

              <button
                onClick={startGame}
                style={{
                  backgroundColor: primaryColor,
                  color: "#000",
                  border: "none",
                  padding: "16px",
                  borderRadius: "12px",
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                }}
              >
                Start Game
              </button>
            </div>
          )}

          {gameState === "playing" && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  marginBottom: "15px",
                }}
              >
                <div
                  style={{
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    padding: "10px 15px",
                    borderRadius: "10px",
                    fontSize: "0.9rem",
                  }}
                >
                  <span style={{ fontWeight: "bold", marginRight: "5px" }}>
                    Attempts:
                  </span>
                  <span>
                    {attempts}/{config.maxAttempts}
                  </span>
                </div>

                <div
                  style={{
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    padding: "10px 15px",
                    borderRadius: "10px",
                    fontSize: "0.9rem",
                    color: timeLeft < 10 ? "#ef4444" : textColor,
                  }}
                >
                  <span style={{ fontWeight: "bold", marginRight: "5px" }}>
                    Time:
                  </span>
                  <span>{formatTime(timeLeft)}</span>
                </div>
              </div>

              <div
                style={{
                  width: "100%",
                  height: "8px",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "4px",
                  marginBottom: "20px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${(attempts / config.maxAttempts) * 100}%`,
                    height: "100%",
                    backgroundColor: primaryColor,
                    transition: "width 0.5s ease",
                  }}
                />
              </div>

              <div
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  padding: "20px",
                  borderRadius: "15px",
                  marginBottom: "20px",
                  width: "100%",
                  textAlign: "center",
                }}
              >
                <p
                  style={{
                    fontSize: "1.1rem",
                    marginBottom: "5px",
                  }}
                >
                  I'm thinking of a number between
                </p>
                <p
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: "bold",
                    marginBottom: "5px",
                  }}
                >
                  {config.min} and {config.max}
                </p>
              </div>

              <div
                style={{
                  width: "100%",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    marginBottom: "15px",
                  }}
                >
                  <input
                    type="number"
                    value={guess}
                    onChange={(e) => setGuess(e.target.value)}
                    placeholder={`Enter a number (${config.min}-${config.max})`}
                    min={config.min}
                    max={config.max}
                    style={{
                      flex: 1,
                      backgroundColor: "rgba(0, 0, 0, 0.3)",
                      color: textColor,
                      border: "2px solid rgba(255, 255, 255, 0.1)",
                      padding: "15px",
                      borderRadius: "12px",
                      fontSize: "1rem",
                      outline: "none",
                      transition: "border-color 0.2s ease",
                    }}
                  />
                  <button
                    onClick={handleGuess}
                    style={{
                      backgroundColor: primaryColor,
                      color: "#000",
                      border: "none",
                      padding: "15px 20px",
                      borderRadius: "12px",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                  >
                    Guess
                  </button>
                </div>

                {feedback && (
                  <div
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                      padding: "15px",
                      borderRadius: "15px",
                      textAlign: "center",
                      marginBottom: "15px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "1rem",
                      }}
                    >
                      {feedback}
                    </p>
                  </div>
                )}
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: "10px",
                }}
              >
                <button
                  onClick={() => {
                    setGameState("setup");
                    setConsecutiveWins(0);
                  }}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: textColor,
                    border: "none",
                    padding: "12px",
                    borderRadius: "12px",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    flex: 1,
                  }}
                >
                  Quit
                </button>

                <button
                  onClick={useHint}
                  disabled={hintUsed}
                  style={{
                    backgroundColor: hintUsed
                      ? "rgba(255, 255, 255, 0.05)"
                      : "rgba(255, 255, 255, 0.1)",
                    color: hintUsed ? secondaryColor : textColor,
                    border: "none",
                    padding: "12px",
                    borderRadius: "12px",
                    fontSize: "0.9rem",
                    cursor: hintUsed ? "not-allowed" : "pointer",
                    transition: "all 0.2s ease",
                    flex: 1,
                  }}
                >
                  Use Hint
                </button>

                <button
                  onClick={startGame}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: textColor,
                    border: "none",
                    padding: "12px",
                    borderRadius: "12px",
                    fontSize: "0.9rem",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    flex: 1,
                  }}
                >
                  Restart
                </button>
              </div>
            </div>
          )}

          {(gameState === "won" || gameState === "lost") && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "20px",
              }}
            >
              <h2
                style={{
                  fontSize: "2rem",
                  fontWeight: "bold",
                  textAlign: "center",
                  color: gameState === "won" ? primaryColor : "#ef4444",
                  marginBottom: "10px",
                }}
              >
                {gameState === "won" ? "You Won!" : "Game Over!"}
              </h2>

              <div
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  padding: "20px",
                  borderRadius: "15px",
                  width: "100%",
                  textAlign: "center",
                  marginBottom: "10px",
                }}
              >
                <p
                  style={{
                    fontSize: "1.1rem",
                    marginBottom: "5px",
                  }}
                >
                  The number was
                </p>
                <p
                  style={{
                    fontSize: "2.5rem",
                    fontWeight: "bold",
                    color: primaryColor,
                  }}
                >
                  {targetNumber}
                </p>
              </div>

              {feedback && (
                <div
                  style={{
                    backgroundColor: "rgba(0, 0, 0, 0.2)",
                    padding: "15px",
                    borderRadius: "15px",
                    textAlign: "center",
                  }}
                >
                  <p
                    style={{
                      fontSize: "1rem",
                    }}
                  >
                    {feedback}
                  </p>
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: "15px",
                  marginTop: "10px",
                }}
              >
                <button
                  onClick={() => setGameState("setup")}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    color: textColor,
                    border: "none",
                    padding: "16px",
                    borderRadius: "12px",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    flex: 1,
                  }}
                >
                  Change Difficulty
                </button>

                <button
                  onClick={startGame}
                  style={{
                    backgroundColor: primaryColor,
                    color: "#000",
                    border: "none",
                    padding: "16px",
                    borderRadius: "12px",
                    fontSize: "1rem",
                    fontWeight: "bold",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    flex: 1,
                  }}
                >
                  Play Again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {(gameState === "playing" ||
        gameState === "won" ||
        gameState === "lost") && (
        <div style={{ padding: "16px", paddingBottom: 0, width: "100%" }}>
          <div
            style={{
              width: "100%",
              maxWidth: "600px",
              backgroundColor: "#1f2937",
              borderRadius: "20px",
              padding: "10px",
              marginBottom: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "1.1rem",
                fontWeight: "bold",
                marginBottom: "15px",
              }}
            >
              Guess History
            </h3>

            {guessHistory.length === 0 ? (
              <p
                style={{
                  fontSize: "0.9rem",
                  color: secondaryColor,
                  textAlign: "center",
                  padding: "15px",
                }}
              >
                No guesses yet
              </p>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    height: "30px",
                    backgroundColor: "rgba(0, 0, 0, 0.3)",
                    borderRadius: "15px",
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: "10px",
                      fontSize: "0.8rem",
                      top: "8px",
                    }}
                  >
                    {config.min}
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      right: "10px",
                      fontSize: "0.8rem",
                      top: "8px",
                    }}
                  >
                    {config.max}
                  </div>

                  {gameState !== "playing" && (
                    <div
                      style={{
                        position: "absolute",
                        left: `${getPercentInRange(targetNumber)}%`,
                        transform: "translateX(-50%)",
                        top: 0,
                        width: "4px",
                        height: "30px",
                        backgroundColor: primaryColor,
                        zIndex: 2,
                      }}
                    />
                  )}

                  {guessHistory.map((guess, index) => (
                    <div
                      key={index}
                      style={{
                        position: "absolute",
                        left: `${getPercentInRange(guess.value)}%`,
                        transform: "translateX(-50%)",
                        //   top: 0,
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        backgroundColor:
                          guess.result === "correct"
                            ? "#4ade80"
                            : guess.result === "too high"
                            ? "#ef4444"
                            : "#60a5fa",
                        top: "9px",
                        zIndex: 2,
                      }}
                    />
                  ))}
                </div>

                {guessHistory
                  .slice()
                  .reverse()
                  .map((guess, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 15px",
                        backgroundColor: "rgba(0, 0, 0, 0.2)",
                        borderRadius: "10px",
                        fontSize: "0.9rem",
                      }}
                    >
                      <span>Guess #{guessHistory.length - index}:</span>
                      <span style={{ fontWeight: "bold" }}>{guess.value}</span>
                      <span
                        style={{
                          color:
                            guess.result === "correct"
                              ? "#4ade80"
                              : guess.result === "too high"
                              ? "#ef4444"
                              : "#60a5fa",
                        }}
                      >
                        {guess.result === "correct" ? "Correct!" : guess.result}
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      )}
      <div
        style={{
          padding: "16px",
          paddingTop: 0,
          width: "100%",
        }}
      >
        <div
          style={{
            width: "100%",
            maxWidth: "600px",
            backgroundColor: "#1f2937",
            borderRadius: "20px",
            padding: "20px",
          }}
        >
          <h3
            style={{
              fontSize: "1.1rem",
              fontWeight: "bold",
              marginBottom: "15px",
            }}
          >
            How to Play
          </h3>

          <ol
            style={{
              paddingLeft: "20px",
              fontSize: "0.9rem",
              color: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <li>Select a difficulty level to begin</li>
            <li>The computer will pick a random number within the range</li>
            <li>Enter your guess and submit</li>
            <li>
              After each guess, you'll get a hint: "too high" or "too low"
            </li>
            <li>Use the hints to narrow down your next guess</li>
            <li>
              Try to find the number before running out of attempts or time
            </li>
            <li>
              Score is based on difficulty, attempts used, time left, and hint
              usage
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
